import { useMemo, useCallback } from 'react';
import type { TreeDataNode } from 'antd';
import { fuzzyScore, filterScoredTree, sortScoredTree, type ScoredTreeNode } from 'utils/scored-tree.utils';

interface UseFilteredTreeDataProps {
  treeData: TreeDataNode[];
  searchValue: string;
  showSearch?: boolean;
  renderTreeNodeTitle: (nodeData: TreeDataNode, originalTitle: string) => React.ReactNode;
}

interface ScoredDataNode extends TreeDataNode, ScoredTreeNode {
  children: ScoredDataNode[];
}

export const useFilteredTreeData = ({
  treeData,
  searchValue,
  showSearch = false,
  renderTreeNodeTitle
}: UseFilteredTreeDataProps) => {
  const enhanceTreeData = useCallback(
    (nodes: TreeDataNode[]): TreeDataNode[] => {
      return nodes.map(node => {
        const nodeTitle = node.title as string;
        const enhancedNode: TreeDataNode = {
          ...node,
          title: renderTreeNodeTitle(node, nodeTitle),
          children: node.children ? enhanceTreeData(node.children) : undefined
        };
        // Store original title for filtering using a custom property
        (enhancedNode as any).originalTitle = node.title;
        return enhancedNode;
      });
    },
    [renderTreeNodeTitle]
  );

  const { filteredTreeData, searchMatchKeys } = useMemo(() => {
    let dataToUse = treeData;
    const matchedKeys: string[] = [];

    if (showSearch && searchValue.trim()) {
      const buildScoredNodes = (nodes: TreeDataNode[]): ScoredDataNode[] => {
        return nodes.map(node => {
          const searchText = (node as any).searchText as string | undefined;
          const children = node.children ? buildScoredNodes(node.children) : [];
          const score = searchText ? fuzzyScore(searchValue, searchText) : 0;
          const propagatedScore = Math.max(score, ...children.map(c => c.propagatedScore), 0);

          if (score > 0 && node.key) {
            matchedKeys.push(String(node.key));
          }

          return { ...node, score, propagatedScore, children };
        });
      };

      const scored = buildScoredNodes(treeData);
      const filtered = filterScoredTree(scored);
      const sorted = sortScoredTree(filtered, node => ((node as any).searchText || '').toLowerCase());
      dataToUse = sorted;
    }

    return {
      filteredTreeData: enhanceTreeData(dataToUse),
      searchMatchKeys: matchedKeys
    };
  }, [treeData, searchValue, showSearch, enhanceTreeData]);

  return { filteredTreeData, searchMatchKeys };
};
